// client/src/coms/organisation/AccountBrowser.js
// Browsing one of your own connected accounts (outside any server): folders, search, open in the provider,
// download, and, when you allowed it, uploads and new folders. Repository providers first ask which
// repository; Google Drive shows your linked files (the ones you chose with Google's file picker or uploaded
// through Voxemera) and offers the picker to link more. Removing a link never touches the file in Drive.
// Everything goes through Voxemera with your own sign-in; the provider's permissions apply.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiGetJson, apiPostJson, directUpload, apiDelete } from './orgApi';
import { formatBytes, formatDate, kindIcon, notify, Spinner, EmptyState, ErrorNote, usePrompt, useConfirm, ProviderBadge } from './orgShared';
import { ItemRow, Breadcrumbs } from './libraryParts';
import { pickDriveFiles, downloadActions, openWhenReady } from './storageAccounts';

export default function AccountBrowser({ conn, onBack, onReconnect }) {
  const api = `/me/storage/connections/${conn.id}`;
  const repoProvider = conn.repositories;
  const [repo, setRepo] = useState('');
  const [repos, setRepos] = useState(null);
  const [repoInput, setRepoInput] = useState('');
  const [stack, setStack] = useState([{ id: '', name: conn.account_label || conn.provider_label }]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [uploads, setUploads] = useState([]);
  const fileInput = useRef(null);
  const [prompt, promptEl] = usePrompt();
  const [confirm, confirmEl] = useConfirm();
  const cur = stack[stack.length - 1];
  const repoQs = repo ? `repo=${encodeURIComponent(repo)}` : '';
  const caps = conn.capabilities || {};
  const canWrite = conn.mode === 'readwrite';
  const drive = conn.provider === 'gdrive';
  const linksView = drive && !cur.id;

  useEffect(() => {
    if (!repoProvider) return;
    apiGetJson(`${api}/repos`).then(setRepos, (e) => setRepos({ error: e }));
  }, [api, repoProvider]);

  const load = useCallback(
    async (cursor) => {
      if (repoProvider && !repo) return;
      setErr(null);
      try {
        if (linksView) {
          const r = await apiGetJson(`${api}/links`);
          setData({ items: r.links, cursor: null, links: true });
          return;
        }
        const qs = new URLSearchParams({ ...(cur.id ? { ref: cur.id } : {}), ...(cursor ? { cursor } : {}), ...(repo ? { repo } : {}) });
        const r = await apiGetJson(`${api}/browse?${qs}`);
        setData((prev) => (cursor && prev ? { ...r, items: [...prev.items, ...r.items] } : r));
      } catch (e) {
        setErr(e);
        if (!cursor) setData(null);
      }
    },
    [api, cur.id, repo, repoProvider, linksView]
  );
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!q.trim() || (repoProvider && !repo)) {
      setResults(null);
      return undefined;
    }
    const t = setTimeout(() => {
      apiGetJson(`${api}/search?q=${encodeURIComponent(q.trim())}${repoQs ? `&${repoQs}` : ''}`).then((r) => setResults(r.items), (e) => setResults({ error: e }));
    }, 400);
    return () => clearTimeout(t);
  }, [q, api, repo, repoQs, repoProvider]);

  async function pick() {
    try {
      const grant = await apiPostJson(`${api}/picker`, {});
      const ids = await pickDriveFiles(grant);
      if (!ids.length) return;
      const r = await apiPostJson(`${api}/picked`, { ids });
      notify(`Linked ${r.items.length} file${r.items.length === 1 ? '' : 's'} from Google Drive${r.refused ? ` (${r.refused} could not be opened with this account)` : ''}`, { tone: 'success' });
      if (stack.length > 1) setStack([stack[0]]);
      else load();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  // Linked files open through Google (checked first, so a file deleted in Drive says so instead of failing there).
  function openLink(it, d) {
    openWhenReady(apiGetJson(`${api}/links/${it.link_id}/open${d && d.format ? `?export=${encodeURIComponent(d.format)}` : ''}`)).catch((e) => {
      notify(e.message, { tone: 'error' });
      if (e.status === 410) load();
    });
  }

  async function unlink(it) {
    const ok = await confirm({
      title: `Remove “${it.name}” from Voxemera?`,
      body: 'Voxemera forgets this link. The file stays in your Google Drive exactly as it is, and nothing is deleted there.',
      confirmLabel: 'Remove from Voxemera',
    });
    if (!ok) return;
    try {
      const r = await apiDelete(`${api}/links/${it.link_id}`);
      notify(r.note, { tone: 'success' });
      load();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function download(it) {
    try {
      const r = await apiGetJson(`${api}/download?ref=${encodeURIComponent(it.ref)}&format=json${repoQs ? `&${repoQs}` : ''}`);
      if (r && r.url) window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      if (e.status === undefined || e.status === 200) window.location.href = `/api/coms${api}/download?ref=${encodeURIComponent(it.ref)}${repoQs ? `&${repoQs}` : ''}`;
      else notify(e.message, { tone: 'error' });
    }
  }

  async function mkdir() {
    const name = await prompt({ title: `New folder in ${conn.provider_label}`, label: 'Folder name', confirmLabel: 'Create' });
    if (!name) return;
    try {
      await apiPostJson(`${api}/folders`, { name, parent_ref: cur.id || '' });
      load();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  function upload(files) {
    for (const file of Array.from(files || [])) {
      const id = `${Date.now()}-${file.name}`;
      setUploads((u) => [...u, { id, name: file.name, progress: 0 }]);
      (async () => {
        try {
          const s = await apiPostJson(`${api}/uploads`, { name: file.name, size: file.size, mime: file.type || 'application/octet-stream', parent_ref: cur.id || '' });
          const job = directUpload(s.upload, file, (pr) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress: pr } : x))));
          setUploads((u) => u.map((x) => (x.id === id ? { ...x, abort: () => { job.abort(); apiDelete(`${api}/uploads/${s.token}`).catch(() => {}); } } : x)));
          const result = await job.promise;
          await apiPostJson(`${api}/uploads/${s.token}/complete`, { result });
          setUploads((u) => u.filter((x) => x.id !== id));
          notify(`Uploaded ${file.name} to ${conn.provider_label}`, { tone: 'success' });
          load();
        } catch (e) {
          setUploads((u) => u.map((x) => (x.id === id ? { ...x, error: e.cancelled ? 'Cancelled' : e.message } : x)));
          setTimeout(() => setUploads((u) => u.filter((x) => x.id !== id)), 9000);
        }
      })();
    }
  }

  const items = results && Array.isArray(results) ? results : data ? data.items : [];
  const code = err && err.data && err.data.code;

  return (
    <div className="vxo-browser vxo-account-browser" data-testid="account-browser">
      <div className="vxo-toolbar">
        <div className="vxo-toolbar-title">
          <h2>
            <ProviderBadge provider={conn.provider} withLabel /> {conn.account_label}
          </h2>
          <span className="vxo-muted">Your own account. {conn.provider_label}’s permissions decide what you can see and change.</span>
        </div>
        <div className="vxo-toolbar-actions">
          {onBack ? (
            <button type="button" className="vx-btn" onClick={onBack}>
              ← Accounts
            </button>
          ) : null}
          {!repoProvider || repo ? <input className="vxo-input" type="search" placeholder={`Search ${conn.provider_label}`} aria-label={`Search ${conn.provider_label}`} value={q} onChange={(e) => setQ(e.target.value)} /> : null}
          {conn.picker ? (
            <button type="button" className="vx-btn" onClick={pick} data-testid="account-picker">
              Choose files from Google Drive
            </button>
          ) : null}
          {canWrite && caps.mkdir && (!repoProvider || repo) ? (
            <button type="button" className="vx-btn" onClick={mkdir}>
              New folder
            </button>
          ) : null}
          {canWrite && caps.upload && !repoProvider ? (
            <>
              <button type="button" className="vx-btn primary" onClick={() => fileInput.current && fileInput.current.click()}>
                Upload
              </button>
              <input ref={fileInput} type="file" multiple hidden onChange={(e) => {
                upload(e.target.files);
                e.target.value = '';
              }} />
            </>
          ) : null}
        </div>
      </div>
      {repoProvider && !repo ? (
        <div className="vxo-card" data-testid="repo-chooser">
          <h3 className="vxo-h3">Choose a {conn.provider === 'github' ? 'repository' : 'project'}</h3>
          {!repos ? <Spinner /> : null}
          {repos && repos.error ? <ErrorNote error={repos.error} /> : null}
          {repos && Array.isArray(repos.repos) && repos.repos.length ? (
            <ul className="vxo-plain vxo-repo-list">
              {repos.repos.map((r) => (
                <li key={r.full_name}>
                  <button type="button" className="vxo-linkbtn" onClick={() => setRepo(r.full_name)}>
                    {r.full_name} <span className="vxo-muted">{r.private ? 'private' : 'public'}{r.can_write ? ' · can commit' : ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {repos && Array.isArray(repos.repos) && !repos.repos.length && conn.provider === 'github' ? (
            <p className="vxo-muted">The Voxemera GitHub App isn’t installed on any repository for this account yet.</p>
          ) : null}
          {repos && repos.install_url ? (
            <p>
              <a className="vx-btn small" href={repos.install_url} target="_blank" rel="noopener noreferrer">
                Choose repositories on GitHub
              </a>{' '}
              <span className="vxo-muted">Voxemera can only see the repositories you choose there.</span>
            </p>
          ) : null}
          {repos && (repos.can_list === false || conn.provider === 'gitlab') ? (
            <form
              className="vxo-row2"
              onSubmit={(e) => {
                e.preventDefault();
                if (repoInput.trim()) setRepo(repoInput.trim());
              }}
            >
              <label className="vxo-field">
                <span>{conn.provider === 'gitlab' ? 'Project path (group/project)' : 'owner/repository'}</span>
                <input value={repoInput} onChange={(e) => setRepoInput(e.target.value)} placeholder={conn.provider === 'gitlab' ? 'my-group/my-project' : 'owner/repo'} />
              </label>
              <button type="submit" className="vx-btn">
                Open
              </button>
            </form>
          ) : null}
        </div>
      ) : (
        <>
          {repoProvider ? (
            <p className="vxo-where">
              {repo}{' '}
              <button type="button" className="vxo-linkbtn" onClick={() => {
                setRepo('');
                setStack([stack[0]]);
                setData(null);
              }}>
                change
              </button>
            </p>
          ) : null}
          <Breadcrumbs crumbs={stack} onGo={(_id, i) => setStack(stack.slice(0, i + 1))} />
          {linksView ? (
            <p className="vxo-hint" data-testid="drive-links-hint">
              Your linked files: the ones you chose with Google’s file picker{canWrite ? ' or uploaded through Voxemera' : ''}. Voxemera keeps only their names and links; the files stay in your Google Drive, and the rest of your Drive stays private. Removing a link never deletes the file.
            </p>
          ) : null}
          {drive && cur.id ? <p className="vxo-hint">Inside a linked folder you see only the files Google lets Voxemera see: ones you chose or created through Voxemera. Open the folder in Google Drive for everything else.</p> : null}
          {code === 'auth' ? (
            <EmptyState icon="⚠️" title="This account needs to be reconnected">
              <p>{conn.provider_label} no longer accepts Voxemera’s sign-in (it expired or access was removed there).</p>
              {onReconnect ? (
                <button type="button" className="vx-btn primary" onClick={() => onReconnect(conn)}>
                  Reconnect {conn.provider_label}
                </button>
              ) : null}
            </EmptyState>
          ) : (
            <ErrorNote error={err} onRetry={() => load()} />
          )}
          {uploads.length ? (
            <ul className="vxo-uploads" aria-label="Uploads">
              {uploads.map((u) => (
                <li key={u.id} className={u.error ? 'is-error' : ''}>
                  <span className="vxo-upload-name">
                    {u.name} → {conn.provider_label}
                  </span>
                  {u.error ? <span className="vxo-upload-err">{u.error}</span> : <progress max={1} value={u.progress} aria-label="Upload progress" />}
                  {!u.error && u.abort ? (
                    <button type="button" className="vx-btn small" onClick={u.abort}>
                      Cancel
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {!data && !err ? <Spinner label={`Loading from ${conn.provider_label}…`} /> : null}
          {results && results.error ? <ErrorNote error={results.error} /> : null}
          {data && !items.length ? (
            <EmptyState icon="📂" title={results ? 'Nothing found' : data.links ? 'No linked files yet' : 'This folder is empty'}>
              {data.links && !results ? <p>{conn.picker ? 'Use “Choose files from Google Drive” to link files to Voxemera.' : 'Google’s file picker is not available on this Voxemera site yet.'}</p> : null}
            </EmptyState>
          ) : null}
          <div className="vxo-items is-list" role="list" data-testid="account-items">
            {items.map((it) => {
              const isLink = !!it.link_id;
              const gone = isLink && it.status !== 'ok';
              const openInDrive = () => (isLink ? openLink(it) : window.open(it.web_url, '_blank', 'noopener,noreferrer'));
              return (
                <ItemRow
                  key={isLink ? `l${it.link_id}` : it.ref}
                  testId={isLink ? `link-${it.link_id}` : undefined}
                  icon={kindIcon(it.mime, { folder: it.is_folder })}
                  provider={conn.provider}
                  name={it.name}
                  badge={gone ? 'Unavailable' : it.google_type || undefined}
                  meta={gone ? it.status_detail : [it.is_folder ? 'Folder' : it.google_type || formatBytes(it.size), it.owner, it.modified_at ? formatDate(it.modified_at) : null, it.path && results ? it.path : null].filter(Boolean).join(' · ')}
                  onOpen={() => (gone ? null : it.is_folder ? (setQ(''), setStack([...stack, { id: it.ref, name: it.name }])) : it.web_url ? openInDrive() : download(it))}
                  actions={[
                    !gone && it.web_url && { label: `Open in ${conn.provider_label}`, onClick: openInDrive },
                    ...(!gone && drive ? downloadActions(it, (d) => (isLink ? openLink(it, d) : window.open(d.url, '_blank', 'noopener,noreferrer'))) : []),
                    !drive && !it.is_folder && caps.download && { label: 'Download', onClick: () => download(it) },
                    isLink && { label: 'Remove from Voxemera', onClick: () => unlink(it) },
                  ]}
                />
              );
            })}
          </div>
          {data && data.cursor && !results ? (
            <button type="button" className="vx-btn vxo-more" onClick={() => load(data.cursor)}>
              Load more
            </button>
          ) : null}
        </>
      )}
      {promptEl}
      {confirmEl}
    </div>
  );
}
